import { mediaQueryLarge, isMobileBreakpoint, prefersReducedMotion } from '@theme/utilities';

// Accordion
// Still extends HTMLElement over Component so that refs are still available to parent components (e.g. SortingFilterComponent)
class AccordionCustom extends HTMLElement {
  /** @type {HTMLDetailsElement} */
  get details() {
    const details = this.querySelector('details');

    if (!(details instanceof HTMLDetailsElement)) throw new Error('Details element not found');

    return details;
  }

  /** @type {HTMLElement} */
  get summary() {
    const summary = this.details.querySelector('summary');

    if (!(summary instanceof HTMLElement)) throw new Error('Summary element not found');

    return summary;
  }

  get #disableOnMobile() {
    return this.dataset.disableOnMobile === 'true';
  }

  get #disableOnDesktop() {
    return this.dataset.disableOnDesktop === 'true';
  }

  get #closeWithEscape() {
    return this.dataset.closeWithEscape === 'true';
  }

  #controller = new AbortController();

  connectedCallback() {
    const { signal } = this.#controller;

    this.#setDefaultOpenState();

    this.addEventListener('keydown', this.#handleKeyDown, { signal });
    this.summary.addEventListener('click', this.handleClick, { signal });
    this.details.addEventListener('toggle', this.#handleToggle, { signal });
    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange, { signal });
  }

  /**
   * Handles the disconnect event.
   */
  disconnectedCallback() {
    // Disconnect all the event listeners
    this.#controller.abort();
  }

  /** Set right before a real click, read (and cleared) by #handleToggle so it
   * can tell a shopper's own click apart from a programmatic open (initial
   * default-open, a breakpoint change) without those also scrolling the
   * page. */
  #userInitiatedToggle = false;

  /**
   * Handles the click event.
   * @param {Event} event - The event.
   */
  handleClick = (event) => {
    const isMobile = isMobileBreakpoint();
    const isDesktop = !isMobile;

    // Stop default behaviour from the browser
    if ((isMobile && this.#disableOnMobile) || (isDesktop && this.#disableOnDesktop)) {
      event.preventDefault();
      return;
    }

    this.#userInitiatedToggle = true;
  };

  /**
   * Handles the media query change event.
   */
  #handleMediaQueryChange = () => {
    this.#setDefaultOpenState();
  };

  /**
   * Sets the default open state of the accordion based on the `open-by-default-on-mobile` and `open-by-default-on-desktop` attributes.
   */
  #setDefaultOpenState() {
    const isMobile = isMobileBreakpoint();

    this.details.open =
      (isMobile && this.hasAttribute('open-by-default-on-mobile')) ||
      (!isMobile && this.hasAttribute('open-by-default-on-desktop')) ||
      this.#isFirstVisibleInOpenFirstRowGroup();
  }

  /**
   * Some accordion groups (e.g. the product page's details/specs accordion)
   * mark themselves `data-open-first-row` so that whichever row ends up
   * first is opened by default, regardless of that row's own setting — a
   * row earlier in the block order can render nothing for a given product
   * (e.g. knife specs on a non-knife product) and disappear via CSS, and
   * the row that's actually first on screen should be the one that opens.
   * @returns {boolean}
   */
  #isFirstVisibleInOpenFirstRowGroup() {
    const group = this.closest('[data-open-first-row]');
    if (!group || getComputedStyle(this).display === 'none') return false;

    // Rows marked data-open-first-defer (the spec sheet on a non-knife
    // product) are passed over for the next visible row, unless no other
    // row is visible. Mirrors the inline script in blocks/accordion.liquid.
    const visible = [...group.querySelectorAll('accordion-custom')].filter(
      (row) => getComputedStyle(row).display !== 'none'
    );
    const target = visible.find((row) => !row.querySelector('[data-open-first-defer]')) ?? visible[0];

    return target === this;
  }

  /**
   * Keeps groups marked `data-open-first-row` (the product page's
   * details/specs accordion) down to one open row at a time: opening a row
   * there closes whichever other row was open, rather than letting several
   * sit open together. Scoped to the group rather than applied everywhere,
   * since other accordions (FAQ, filters) are meant to allow several rows
   * open at once.
   *
   * Also marks the group `data-accordion-interacted` the first time this
   * runs. accordion-custom-component.liquid and _accordion-row.liquid only
   * animate a row that's :focus-within, on purpose — a row opened by the
   * page itself on load should snap in, not visibly pop open. But the
   * sibling this closes was never focused (focus went to the row the
   * shopper actually clicked), so without this marker that same rule
   * makes it flash shut instead of easing closed. Once a shopper has
   * driven the group at all, every row in it is fair game to animate,
   * focused or not — see the matching `[data-accordion-interacted]`
   * selector in _accordion-row.liquid.
   *
   * Finally, opening a row scrolls the *group* — every row's heading, not
   * just the one a shopper clicked — to the top of the viewport, rather
   * than staying put while content grows below the fold. Scrolling to the
   * clicked row alone (an earlier version of this) pushed any rows above
   * it off the top of the screen along with it, so a shopper who opened
   * the third row lost easy access to the first two; scrolling to the
   * group keeps every row's heading reachable regardless of which one is
   * open. Gated on #userInitiatedToggle so this never fires for the
   * initial default-open row or a breakpoint change, only an actual click.
   *
   * Scrolls `group` itself, not the summary of the row that was clicked:
   * _accordion-row.liquid collapses an open row's summary to near-zero
   * height the instant [open] applies (min-height: 0, padding-block: 0 —
   * the heading text is hidden and the caret becomes a floating corner
   * control), and that collapse is already in effect by the time this
   * toggle handler runs, since scrollIntoView forces the layout flush that
   * applies it. Scrolling that collapsing element was landing on a moving
   * target. The group's own height isn't touched by any of that, so it's
   * a stable thing to align to the viewport.
   *
   * Waits out the single-open close this same toggle just triggered above
   * before scrolling, rather than one requestAnimationFrame: scrollIntoView
   * computes and commits a scroll position from the layout at the moment
   * it's called, and one frame in, the row that's being auto-closed hasn't
   * shrunk yet -- it's still its full open height, mid --accordion-duration
   * (0.32s). Scrolling against that transient, taller layout landed on a
   * position that made sense for a frame and then didn't: the closing row
   * kept shrinking after the scroll had already committed, so the page
   * ended up shorter than what the scroll had targeted and the viewport
   * overshot past the accordion entirely. Matches --accordion-duration
   * with a little headroom so it's scrolling against the settled layout,
   * not a mid-transition one. Reduced-motion visitors get there just as
   * fast either way, since accordion-custom-component.liquid and
   * _accordion-row.liquid already drop the transition to instant for them
   * -- the wait just means scrolling to a layout that finished sooner. */
  static #POST_TOGGLE_SCROLL_DELAY = 340;

  #handleToggle = () => {
    const userInitiated = this.#userInitiatedToggle;
    this.#userInitiatedToggle = false;

    if (!this.details.open) return;

    const group = this.closest('[data-open-first-row]');
    if (!group) return;

    group.setAttribute('data-accordion-interacted', 'true');

    for (const row of group.querySelectorAll('accordion-custom')) {
      if (row !== this && row.details.open) row.details.open = false;
    }

    if (userInitiated) {
      setTimeout(() => {
        group.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'start' });
      }, AccordionCustom.#POST_TOGGLE_SCROLL_DELAY);
    }
  };

  /**
   * Handles keydown events for the accordion
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    // Close the accordion when used as a menu
    if (event.key === 'Escape' && this.#closeWithEscape) {
      event.preventDefault();

      this.details.open = false;
      this.summary.focus();
    }
  }
}

if (!customElements.get('accordion-custom')) {
  customElements.define('accordion-custom', AccordionCustom);
}
